import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getOrders } from '../../services/api';
import websocket from '../../services/websocket';
import { formatCurrency } from '../../utils/formatCurrency';
import { storage } from '../../utils/storage';
import './DockWidgets.css';

const ActiveOrdersWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadOrders();

    // Connect WebSocket and listen for real-time updates
    const restaurantId = storage.get('selectedRestaurantId');

    const handleNewOrder = (data) => {
      console.log('[ActiveOrdersWidget] New order received:', data);
      loadOrders(); // Reload orders immediately
    };

    const handleOrderStatusChanged = (data) => {
      console.log('[ActiveOrdersWidget] Order status changed:', data);
      loadOrders(); // Reload orders immediately
    };

    if (restaurantId) {
      websocket.connect(restaurantId);
      websocket.on('NEW_ORDER', handleNewOrder);
      websocket.on('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
    }

    // Fallback: refresh every 30s in case WebSocket fails
    const interval = setInterval(loadOrders, 30000);

    return () => {
      clearInterval(interval);
      websocket.off('NEW_ORDER', handleNewOrder);
      websocket.off('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
      // Note: Don't disconnect WebSocket here as other components may be using it
    };
  }, []);

  const loadOrders = async () => {
    try {
      setError(false);
      const data = await getOrders();

      // Helper to check if date is today (local timezone)
      const isToday = (dateString) => {
        const today = new Date();
        const orderDate = new Date(dateString);

        // Compare dates in local timezone
        return (
          orderDate.getDate() === today.getDate() &&
          orderDate.getMonth() === today.getMonth() &&
          orderDate.getFullYear() === today.getFullYear()
        );
      };

      // Helper to check if order is within last 24 hours
      const isWithin24Hours = (dateString) => {
        const orderDate = new Date(dateString);
        const now = new Date();
        const hoursDiff = (now - orderDate) / (1000 * 60 * 60);
        return hoursDiff <= 24;
      };

      const activeOrders = (data.orders || []).filter((order) => {
        // Only consider orders in active status
        const isActiveStatus = ['pending', 'preparing', 'ready'].includes(order.status);
        if (!isActiveStatus) return false;

        // Show orders from today
        if (isToday(order.createdAt)) return true;

        // For orders from previous days (e.g., nocturnal orders after midnight),
        // only show if they're still within 24 hours AND still active
        // This prevents old orders from appearing if they were left open by mistake
        return isWithin24Hours(order.createdAt);
      });

      setOrders(activeOrders.slice(0, 5)); // Show max 5
    } catch (err) {
      // Treat 404 as empty data (restaurant with no orders yet)
      if (err.response?.status === 404) {
        setOrders([]);
      } else {
        console.error('[ActiveOrdersWidget] Error loading orders:', err);
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'preparing':
        return 'status-preparing';
      case 'ready':
        return 'status-ready';
      default:
        return '';
    }
  };

  const getStatusText = (status) => {
    const map = {
      pending: t('orders.pending'),
      preparing: t('orders.preparing'),
      ready: t('orders.ready'),
      delivered: t('orders.delivered'),
      cancelled: t('orders.cancelled'),
    };
    return map[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return 'schedule';
      case 'preparing':
        return 'restaurant';
      case 'ready':
        return 'check_circle';
      default:
        return 'receipt';
    }
  };

  if (loading) {
    return (
      <div className="widget-loading">
        <span className="material-icons spinning">sync</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="widget-error">
        <span className="material-icons">cloud_off</span>
        <span>{t('dock.loadError', 'Error al cargar datos')}</span>
        <button type="button" className="widget-action-sm" onClick={loadOrders}>
          {t('common.retry', 'Reintentar')}
        </button>
      </div>
    );
  }

  return (
    <div className="active-orders-widget">
      {orders.length === 0 ? (
        <div className="widget-empty">
          <span className="material-icons">inbox</span>
          <span>{t('dock.noActiveOrders', 'Sin pedidos activos')}</span>
        </div>
      ) : (
        <>
          <div className="widget-list">
            {orders.map((order) => (
              <div
                key={order.orderId}
                className="widget-list-item"
                onClick={() => navigate('/orders')}
              >
                <div className="order-info">
                  <span className="order-table">
                    {order.orderType === 'TAKEAWAY'
                      ? t('orders.takeaway', 'Takeaway')
                      : `${t('common.table', 'Mesa')} ${order.tableNumber}`}
                  </span>
                  <span className="order-total">{formatCurrency(order.total)}</span>
                </div>
                <span
                  className={`material-icons order-status ${getStatusColor(order.status)}`}
                  title={getStatusText(order.status)}
                >
                  {getStatusIcon(order.status)}
                </span>
              </div>
            ))}
          </div>
          <button type="button" className="widget-action" onClick={() => navigate('/orders')}>
            {t('dock.viewAll', 'Ver todos')}
            <span className="material-icons">arrow_forward</span>
          </button>
        </>
      )}
    </div>
  );
};

export default ActiveOrdersWidget;
